import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import CampaignList from '../components/campaigns/CampaignList';
import ReviewerCampaignList from '../components/campaigns/ReviewerCampaignList';
import EventsList from '../components/events/EventsList';
import ReviewerEventList from '../components/events/ReviewerEventList';
import { useCampaigns } from '../hooks/useCampaigns';
import { useSharedCampaigns } from '../hooks/useSharedCampaigns';
import { useSharedEvents } from '../hooks/useSharedEvents';
import { useAuth } from '../contexts/AuthContext';

/**
 * Primary workspace switcher: a segmented toggle between the two peer
 * surfaces (Campaigns / Events). Sits where the page H1 used to live so the
 * entry point is always in the same, zero-hunt spot. Kept visually distinct
 * from the white pill CTA by using a subtle filled active segment.
 */
const WorkspaceSwitcher = ({ view, onChange }) => {
  const items = [
    { key: 'campaigns', label: 'Campaigns' },
    { key: 'events', label: 'Events' },
  ];
  return (
    <HStack
      spacing={1}
      bg="rgba(255,255,255,0.04)"
      border="1px solid rgba(255,255,255,0.10)"
      borderRadius="full"
      p="3px"
    >
      {items.map((item) => {
        const active = view === item.key;
        return (
          <Button
            key={item.key}
            size="sm"
            h="32px"
            px={5}
            borderRadius="full"
            fontSize="sm"
            fontWeight={active ? '700' : '500'}
            letterSpacing="-0.01em"
            bg={active ? 'rgba(255,255,255,0.10)' : 'transparent'}
            color={active ? '#fafafa' : '#a3a3a3'}
            _hover={active ? {} : { color: '#fafafa', bg: 'rgba(255,255,255,0.04)' }}
            _focus={{ boxShadow: 'none' }}
            onClick={() => onChange(item.key)}
          >
            {item.label}
          </Button>
        );
      })}
    </HStack>
  );
};

/**
 * HomePage layout
 *
 * - Tab 0: "Your Campaigns" — campaigns the current user owns. Always visible.
 * - Tab 1: "Shared with me" — campaigns the current user has accepted
 *   compliance-review invites to. Hidden entirely when the reviewer-shares
 *   list is empty so brand-new users don't see a stub tab. Auto-selected
 *   when the URL carries `?tab=reviews`, which the accept handshake uses
 *   to land the reviewer here after binding.
 *
 * The owner's "New Campaign" CTA only renders on the owner tab — the
 * reviewer tab is intentionally read-only.
 */
const HomePage = () => {
  const { data: campaigns, isLoading, error, refetch } = useCampaigns();
  const {
    data: sharedCampaigns,
    isLoading: isSharedLoading,
    error: sharedError,
  } = useSharedCampaigns();
  const {
    data: sharedEvents,
    isLoading: isSharedEventsLoading,
    error: sharedEventsError,
  } = useSharedEvents();
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const hasShares = Array.isArray(sharedCampaigns) && sharedCampaigns.length > 0;
  const showReviewsTab = hasShares || isSharedLoading;

  const hasEventShares = Array.isArray(sharedEvents) && sharedEvents.length > 0;
  const showEventReviewsTab = hasEventShares || isSharedEventsLoading;

  // Both the workspace view (campaigns / events) and the campaign sub-tab are
  // URL-driven (no useState mirror) so refreshes / deep links / the post-
  // accept redirect all consistently land on the right place.
  const { requestedView, requestedTab } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return { requestedView: params.get('view'), requestedTab: params.get('tab') };
  }, [location.search]);

  const view = requestedView === 'events' ? 'events' : 'campaigns';
  const tabIndex = requestedTab === 'reviews' && showReviewsTab ? 1 : 0;
  const eventTabIndex = requestedTab === 'reviews' && showEventReviewsTab ? 1 : 0;

  const handleViewChange = (next) => {
    if (next === 'events') {
      navigate('/?view=events', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  // Force refetch campaigns when the component mounts or when user changes
  useEffect(() => {
    refetch();
  }, [refetch, user?.id]);

  // Handle payment success/cancel messages and subscription changes from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('payment_success') === 'true') {
      toast({
        title: 'Payment Successful!',
        description: 'Your credits have been added to your account.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      window.history.replaceState({}, document.title, window.location.pathname);
      window.dispatchEvent(new Event('exportCompleted'));
    } else if (urlParams.get('payment_canceled') === 'true') {
      toast({
        title: 'Payment Canceled',
        description: 'You can purchase credits anytime from the billing page.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (urlParams.get('upgrade') === 'true') {
      toast({
        title: 'Subscription Upgraded! 🎉',
        description: "Your plan has been upgraded successfully with proration. You've been charged for the remaining time at the new rate.",
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
      window.history.replaceState({}, document.title, window.location.pathname);
      window.dispatchEvent(new Event('subscriptionUpgraded'));
    } else if (urlParams.get('downgrade') === 'true') {
      toast({
        title: 'Downgrade Scheduled',
        description: "Your plan will be downgraded at the end of your current billing period. You'll continue to have access to your current plan features until then.",
        status: 'info',
        duration: 6000,
        isClosable: true,
      });
      window.history.replaceState({}, document.title, window.location.pathname);
      window.dispatchEvent(new Event('subscriptionDowngraded'));
    }
  }, [toast]);

  const handleTabChange = (idx) => {
    const params = new URLSearchParams(location.search);
    if (idx === 1) {
      params.set('tab', 'reviews');
    } else {
      params.delete('tab');
    }
    const qs = params.toString();
    navigate(qs ? `${location.pathname}?${qs}` : location.pathname, { replace: true });
  };

  return (
    <Box px={0} maxW="100%">
      <Flex
        align="center"
        justify="space-between"
        flexWrap="wrap"
        rowGap={3}
        columnGap={6}
        w="100%"
        mb={6}
        mt={1}
        px={1}
      >
        <WorkspaceSwitcher view={view} onChange={handleViewChange} />

        {view === 'events' ? (
          eventTabIndex === 0 && (
            <Button
              onClick={() => navigate('/events/new')}
              leftIcon={<FiPlus />}
              bg="#fafafa"
              color="#0a0a0a"
              borderRadius="full"
              fontWeight="600"
              fontSize="sm"
              h="36px"
              flexShrink={0}
              _hover={{ opacity: 0.85 }}
            >
              Create Event
            </Button>
          )
        ) : (
          tabIndex === 0 && (
            <Button
              onClick={() => navigate('/campaign/new')}
              leftIcon={<FiPlus />}
              bg="#fafafa"
              color="#0a0a0a"
              borderRadius="full"
              fontWeight="600"
              fontSize="sm"
              h="36px"
              flexShrink={0}
              _hover={{ opacity: 0.85 }}
            >
              New Campaign
            </Button>
          )
        )}
      </Flex>

      {view === 'events' ? (
        showEventReviewsTab ? (
          <Tabs index={eventTabIndex} onChange={handleTabChange} variant="unstyled" isLazy>
            <TabList mb={5} px={1} pt={1} borderBottom="1px solid rgba(255,255,255,0.08)" gap={1}>
              <Tab
                fontSize="sm"
                fontWeight="500"
                color="#a3a3a3"
                px={3}
                py={3}
                borderBottom="2px solid transparent"
                mb="-1px"
                _selected={{ color: '#fafafa', borderBottomColor: '#fafafa', fontWeight: 600 }}
                _hover={{ color: '#fafafa' }}
              >
                My events
              </Tab>
              <Tab
                fontSize="sm"
                fontWeight="500"
                color="#a3a3a3"
                px={3}
                py={3}
                borderBottom="2px solid transparent"
                mb="-1px"
                _selected={{ color: '#fafafa', borderBottomColor: '#fafafa', fontWeight: 600 }}
                _hover={{ color: '#fafafa' }}
              >
                <Flex align="center" gap={2}>
                  <span>Shared with me</span>
                  {hasEventShares && (
                    <Badge
                      bg="rgba(122, 226, 161, 0.12)"
                      color="rgba(122, 226, 161, 0.95)"
                      border="1px solid rgba(122, 226, 161, 0.22)"
                      borderRadius="full"
                      fontSize="10px"
                      fontWeight="600"
                      px={2}
                      textTransform="none"
                    >
                      {sharedEvents.length}
                    </Badge>
                  )}
                </Flex>
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0} pt={2}>
                <EventsList />
              </TabPanel>
              <TabPanel px={0} pt={2}>
                <ReviewerEventList
                  shares={sharedEvents}
                  isLoading={isSharedEventsLoading}
                  error={sharedEventsError}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        ) : (
          <EventsList />
        )
      ) : showReviewsTab ? (
        <Tabs
          index={tabIndex}
          onChange={handleTabChange}
          variant="unstyled"
          isLazy
        >
          <TabList
            mb={5}
            px={1}
            pt={1}
            borderBottom="1px solid rgba(255,255,255,0.08)"
            gap={1}
          >
            <Tab
              fontSize="sm"
              fontWeight="500"
              color="#a3a3a3"
              px={3}
              py={3}
              borderBottom="2px solid transparent"
              mb="-1px"
              _selected={{
                color: '#fafafa',
                borderBottomColor: '#fafafa',
                fontWeight: 600,
              }}
              _hover={{ color: '#fafafa' }}
            >
              My campaigns
            </Tab>
            <Tab
              fontSize="sm"
              fontWeight="500"
              color="#a3a3a3"
              px={3}
              py={3}
              borderBottom="2px solid transparent"
              mb="-1px"
              _selected={{
                color: '#fafafa',
                borderBottomColor: '#fafafa',
                fontWeight: 600,
              }}
              _hover={{ color: '#fafafa' }}
            >
              <Flex align="center" gap={2}>
                <span>Shared with me</span>
                {hasShares && (
                  <Badge
                    bg="rgba(122, 226, 161, 0.12)"
                    color="rgba(122, 226, 161, 0.95)"
                    border="1px solid rgba(122, 226, 161, 0.22)"
                    borderRadius="full"
                    fontSize="10px"
                    fontWeight="600"
                    px={2}
                    textTransform="none"
                  >
                    {sharedCampaigns.length}
                  </Badge>
                )}
              </Flex>
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0} pt={2}>
              <CampaignList
                campaigns={campaigns}
                isLoading={isLoading}
                error={error}
              />
            </TabPanel>
            <TabPanel px={0} pt={2}>
              <ReviewerCampaignList
                shares={sharedCampaigns}
                isLoading={isSharedLoading}
                error={sharedError}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      ) : (
        <CampaignList
          campaigns={campaigns}
          isLoading={isLoading}
          error={error}
        />
      )}

    </Box>
  );
};

export default HomePage;
